/**
 * useFeeCalculation - Hook for calculating withdrawal fees
 * 
 * Based on Bridge API documentation:
 * https://apidocs.bridge.xyz/platform/orchestration/fees-and-mins/devfees
 * 
 * Fee structure:
 * - Developer fee (2%) - platform fee
 * - Payment rail fee (ACH, SEPA, Wire) - for bank withdrawals
 * - Exchange fee - built into rate for currency conversions
 * - Network fee (gas) - ONLY for crypto-to-crypto transfers
 * 
 * IMPORTANT: For offramps (wallet → bank), Bridge ABSORBS network fees.
 * Formula: final_amount = initial_amount - developer_fee - exchange_fee
 * 
 * For crypto withdrawals to external addresses, network fees apply.
 */

import { useMemo } from 'react';
import { BRIDGE_CONFIG, PaymentRail } from '@/lib/bridgeConfig';

export interface FeeBreakdown {
  developerFee: number;
  developerFeePercent: number;
  railFee: number;
  networkFee: number;
  exchangeSpread: number;
  totalFee: number;
  youReceive: number;
  feeDetails: {
    label: string;
    amount: number;
    description?: string;
  }[];
  // Fee warning system
  feePercentage: number; // What % of amount goes to fees
  isHighFee: boolean; // True if fees > 20% of amount
  isCriticalFee: boolean; // True if fees > 40% of amount
  suggestedNetwork?: string; // Cheaper network recommendation
  potentialSavings?: number; // How much user would save on cheaper network
}

interface UseFeeCalculationOptions {
  amount: number;
  currency: string;
  destinationType: 'bank' | 'crypto';
  paymentRail?: PaymentRail | string;
  network?: string;
  exchangeRate?: number; // For cross-currency conversions
}

/**
 * Calculate fees for a withdrawal
 */
export function calculateWithdrawalFees(options: UseFeeCalculationOptions): FeeBreakdown {
  const {
    amount,
    currency,
    destinationType,
    paymentRail,
    network,
    exchangeRate = 1,
  } = options;

  if (!amount || amount <= 0) {
    return {
      developerFee: 0,
      developerFeePercent: BRIDGE_CONFIG.DEVELOPER_FEE_RATE * 100,
      railFee: 0,
      networkFee: 0,
      exchangeSpread: 0,
      totalFee: 0,
      youReceive: 0,
      feeDetails: [],
      feePercentage: 0,
      isHighFee: false,
      isCriticalFee: false,
    };
  }

  const feeDetails: FeeBreakdown['feeDetails'] = [];

  // 1. Developer fee (2%)
  const developerFeePercent = BRIDGE_CONFIG.DEVELOPER_FEE_RATE * 100;
  const developerFee = amount * BRIDGE_CONFIG.DEVELOPER_FEE_RATE;
  
  if (developerFee > 0) {
    feeDetails.push({
      label: 'Service fee',
      amount: developerFee,
      description: `${developerFeePercent}% platform fee`,
    });
  }

  // 2. Payment rail fee (for bank withdrawals)
  let railFee = 0;
  if (destinationType === 'bank' && paymentRail) {
    const normalizedRail = paymentRail.toUpperCase() as keyof typeof BRIDGE_CONFIG.RAIL_FEES;
    const railConfig = BRIDGE_CONFIG.RAIL_FEES[normalizedRail];
    
    if (railConfig) {
      railFee = railConfig.fixed + (amount * railConfig.percent);
      
      // Apply min/max
      if (railConfig.minFee && railFee < railConfig.minFee) {
        railFee = railConfig.minFee;
      }
      if (railConfig.maxFee && railFee > railConfig.maxFee) {
        railFee = railConfig.maxFee;
      }
      
      if (railFee > 0) {
        feeDetails.push({
          label: `${normalizedRail} transfer fee`,
          amount: railFee,
          description: railConfig.fixed > 0 
            ? `Fixed $${railConfig.fixed} fee`
            : railConfig.percent > 0 
              ? `${railConfig.percent * 100}% fee`
              : undefined,
        });
      }
    }
  }

  // 3. Network fee (ONLY for crypto-to-crypto transfers)
  // Per Bridge docs: For offramps (wallet → bank), Bridge ABSORBS network fees (gas_fee = 0)
  // Network fees only apply when sending crypto to external wallet addresses
  let networkFee = 0;
  if (destinationType === 'crypto' && network) {
    const normalizedNetwork = network.toLowerCase() as keyof typeof BRIDGE_CONFIG.NETWORK_FEES;
    const networkConfig = BRIDGE_CONFIG.NETWORK_FEES[normalizedNetwork];
    
    if (networkConfig) {
      networkFee = networkConfig.estimated;
      
      // Convert to target currency if needed
      if (currency !== 'USD' && exchangeRate !== 1) {
        networkFee = networkFee * exchangeRate;
      }
      
      feeDetails.push({
        label: `${network.charAt(0).toUpperCase() + network.slice(1)} network fee`,
        amount: networkFee,
        description: 'Estimated gas fee (actual varies by network congestion)',
      });
    }
  }
  
  // Note: For bank withdrawals (offramp), no network fee is charged
  // Bridge handles the on-chain → fiat conversion internally

  // 4. Exchange spread (for cross-currency, typically ~0.1% built into rate)
  // This is already reflected in the exchange rate, so we don't add it again
  const exchangeSpread = 0;

  // Calculate total
  const totalFee = developerFee + railFee + networkFee + exchangeSpread;
  const youReceive = Math.max(0, amount - totalFee);
  
  // Fee warning calculations
  const feePercentage = amount > 0 ? (totalFee / amount) * 100 : 0;
  const isHighFee = feePercentage > 20; // Warning threshold
  const isCriticalFee = feePercentage > 40; // Critical threshold
  
  // Suggest cheaper network for crypto withdrawals
  let suggestedNetwork: string | undefined;
  let potentialSavings: number | undefined;
  
  if (destinationType === 'crypto' && network && networkFee > 0) {
    // Find the cheapest network
    const cheapNetworks = [
      { name: 'polygon', fee: BRIDGE_CONFIG.NETWORK_FEES.polygon?.estimated || 0 },
      { name: 'base', fee: BRIDGE_CONFIG.NETWORK_FEES.base?.estimated || 0 },
      { name: 'arbitrum', fee: BRIDGE_CONFIG.NETWORK_FEES.arbitrum?.estimated || 0 },
      { name: 'optimism', fee: BRIDGE_CONFIG.NETWORK_FEES.optimism?.estimated || 0 },
    ].sort((a, b) => a.fee - b.fee);
    
    const currentNetworkFee = BRIDGE_CONFIG.NETWORK_FEES[network.toLowerCase() as keyof typeof BRIDGE_CONFIG.NETWORK_FEES]?.estimated || 0;
    const cheapestNetwork = cheapNetworks[0];
    
    // Only suggest if savings are significant (> $1)
    if (cheapestNetwork && currentNetworkFee - cheapestNetwork.fee > 1) {
      suggestedNetwork = cheapestNetwork.name;
      potentialSavings = currentNetworkFee - cheapestNetwork.fee;
    }
  }

  return {
    developerFee,
    developerFeePercent,
    railFee,
    networkFee,
    exchangeSpread,
    totalFee,
    youReceive,
    feeDetails,
    feePercentage,
    isHighFee,
    isCriticalFee,
    suggestedNetwork,
    potentialSavings,
  };
}

/**
 * Hook for fee calculation with memoization
 */
export function useFeeCalculation(options: UseFeeCalculationOptions): FeeBreakdown {
  return useMemo(() => calculateWithdrawalFees(options), [
    options.amount,
    options.currency,
    options.destinationType,
    options.paymentRail,
    options.network,
    options.exchangeRate,
  ]);
}

/**
 * Get estimated arrival time for a payment rail
 */
export function getEstimatedArrival(rail: string, destinationType: 'bank' | 'crypto'): string {
  if (destinationType === 'crypto') {
    return '~5-30 minutes';
  }

  const normalizedRail = rail.toUpperCase() as keyof typeof BRIDGE_CONFIG.RAIL_FEES;
  const railConfig = BRIDGE_CONFIG.RAIL_FEES[normalizedRail];
  
  return railConfig?.eta || '1-5 business days';
}

/**
 * Format fee amount with currency symbol
 */
export function formatFee(amount: number, currency: string): string {
  const symbols: Record<string, string> = {
    USD: '$',
    EUR: '€',
    MXN: 'MX$',
    BRL: 'R$',
  };
  
  const symbol = symbols[currency.toUpperCase()] || '$';
  
  if (amount === 0) {
    return 'Free';
  }
  
  return `${symbol}${amount.toFixed(2)}`;
}

/**
 * Get minimum withdrawal amount based on destination type and currency/asset/network
 */
export function getWithdrawalMinimum(options: {
  destinationType: 'bank' | 'crypto';
  currency?: string;
  asset?: string;
  network?: string;
}): number {
  const { destinationType, currency, asset, network } = options;

  if (destinationType === 'bank') {
    const bankMinimums = BRIDGE_CONFIG.WITHDRAWAL_MINIMUMS.bank;
    const normalizedCurrency = currency?.toUpperCase() as keyof typeof bankMinimums;
    return bankMinimums[normalizedCurrency] || 10;
  }

  // For crypto, consider both asset and network minimums
  const cryptoMinimums = BRIDGE_CONFIG.WITHDRAWAL_MINIMUMS.crypto;
  const networkMinimums = BRIDGE_CONFIG.WITHDRAWAL_MINIMUMS.network;

  const assetMin = asset 
    ? cryptoMinimums[asset.toUpperCase() as keyof typeof cryptoMinimums] || 1
    : 1;

  const networkMin = network 
    ? networkMinimums[network.toLowerCase() as keyof typeof networkMinimums] || 1
    : 1;

  // Return the higher of asset or network minimum
  return Math.max(assetMin, networkMin);
}

/**
 * Check if amount meets minimum for withdrawal
 */
export function meetsMinimum(
  amount: number, 
  currency: string, 
  destinationType: 'bank' | 'crypto',
  options?: { asset?: string; network?: string }
): { meets: boolean; minimum: number } {
  const minimum = getWithdrawalMinimum({
    destinationType,
    currency,
    asset: options?.asset,
    network: options?.network,
  });
  
  return {
    meets: amount >= minimum,
    minimum,
  };
}

